import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import FilterTabs from '../../components/ui/FilterTabs';
import { TrashIcon, SpinnerIcon } from '../../components/ui/Icons';
import ConfirmModal from '../../components/ui/ConfirmModal';
import PanelCard from '../../components/ui/PanelCard';
import CopyButton from '../../components/ui/CopyButton';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Pagination from '../../components/ui/Pagination';

const FILTERS = [
  { id: '', label: 'All' },
  { id: 'false', label: 'Available' },
  { id: 'true', label: 'Used' },
];

const LIMIT = 20;

export default function InvitesTab() {
  const { toast } = useToast();
  const [invites, setInvites] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastCode, setLastCode] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteInvite, setConfirmDeleteInvite] = useState(null);

  const fetchInvites = useCallback(async (p = page, f = filter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (f !== '') params.set('used', f);
      const res = await api.get(`/owner/invites?${params}`);
      setInvites(res.data.invites);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load invite codes.');
    } finally {
      setLoading(false);
    }
  }, [page, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const handleFilter = (f) => { setFilter(f); setPage(1); fetchInvites(1, f); };
  const handlePage = (p) => { setPage(p); fetchInvites(p, filter); };

  const generateCode = async () => {
    setGenerating(true);
    setLastCode(null);
    try {
      const res = await api.post('/owner/invites/generate');
      setLastCode(res.data.code);
      toast.success('Invite code generated.');
      fetchInvites(1, filter);
      setPage(1);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error generating invite code.');
    } finally {
      setGenerating(false);
    }
  };

  const deleteCode = async (invite) => {
    setConfirmDeleteInvite(null);
    setDeletingId(invite.id);
    try {
      await api.delete(`/owner/invites/${invite.id}`);
      toast.success('Invite code deleted.');
      fetchInvites(page, filter);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error deleting invite code.');
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <PanelCard
        title="Generate Invite Code"
        subtitle="Create a one-time invite code instantly, without a user request."
        className="mb-6"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={generateCode} disabled={generating} className="btn-primary">
            {generating ? 'Generating…' : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                     strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M4 8V6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 000 4v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4a2 2 0 000-4z" />
                  <path d="M14 4v16" />
                </svg>
                Generate Code
              </>
            )}
          </button>
          {lastCode && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl
                            bg-brand-500/[0.08] border border-brand-400/25 animate-scale-in">
              <code className="text-brand-100 font-mono text-sm tracking-widest">{lastCode}</code>
              <CopyButton value={lastCode} variant="icon" />
            </div>
          )}
        </div>
      </PanelCard>

      <PanelCard
        flush
        title="All Invite Codes"
        subtitle={`${total} total`}
        actions={<FilterTabs filters={FILTERS} current={filter} onChange={handleFilter} />}
      >
        {/* Mobilon kártyák: a hat oszlop 375 px-en ~300 px oldalra görgetést kért,
            és a törlés gomb a görgetés legvégén volt. */}
        <div className="md:hidden divide-y divide-white/[0.05]">
          {loading ? (
            <div className="py-12 flex justify-center"><LoadingSpinner /></div>
          ) : invites.length === 0 ? (
            <p className="px-5 py-8 text-center text-gray-500 text-sm">No invite codes found.</p>
          ) : invites.map(inv => (
            <div key={inv.id} className="px-4 py-3">
              <div className="flex items-center gap-2">
                <code className="font-mono text-brand-200 text-xs tracking-widest">{inv.code}</code>
                <CopyButton value={inv.code} variant="icon" />
                <span className="ml-auto">
                  {inv.used_by_name
                    ? <span className="badge-inactive">Used</span>
                    : <span className="badge-active">Available</span>}
                </span>
              </div>
              <div className="mt-1.5 flex items-center gap-x-3 gap-y-1.5 flex-wrap text-xs text-gray-500">
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${
                  inv.request_id
                    ? 'bg-white/[0.05] border-white/[0.08] text-gray-400'
                    : 'bg-brand-500/12 border-brand-400/25 text-brand-200'
                }`}>
                  {inv.request_id ? 'Request' : 'Owner'}
                </span>
                <span className="font-mono">{new Date(inv.created_at).toLocaleDateString()}</span>
                {inv.used_by_name && <span>used by <span className="text-gray-300">{inv.used_by_name}</span></span>}
                {!inv.used_by_name && (
                  <button onClick={() => setConfirmDeleteInvite(inv)} disabled={deletingId === inv.id}
                    className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md
                               text-gray-500 hover:text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                    {deletingId === inv.id
                      ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                      : <TrashIcon className="w-3.5 h-3.5" />}
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="scroll-x hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/[0.06]">
              <tr>
                {['Code', 'Source', 'Status', 'Created', 'Used By', ''].map(h => (
                  <th key={h} className="px-5 py-3 text-left table-head">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-12">
                  <div className="flex justify-center"><LoadingSpinner /></div>
                </td></tr>
              ) : invites.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-500 text-sm">
                  No invite codes found.
                </td></tr>
              ) : invites.map(inv => (
                <tr key={inv.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <code className="font-mono text-brand-200 text-xs tracking-widest">{inv.code}</code>
                      <CopyButton value={inv.code} variant="icon" />
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${
                      inv.request_id
                        ? 'bg-white/[0.05] border-white/[0.08] text-gray-400'
                        : 'bg-brand-500/12 border-brand-400/25 text-brand-200'
                    }`}>
                      {inv.request_id ? 'Request' : 'Owner'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {inv.used_by_name
                      ? <span className="badge-inactive">Used</span>
                      : <span className="badge-active">Available</span>}
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs font-mono">
                    {new Date(inv.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-gray-300 text-xs">
                    {inv.used_by_name || <span className="text-gray-700">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    {!inv.used_by_name && (
                      <button onClick={() => setConfirmDeleteInvite(inv)} disabled={deletingId === inv.id}
                        title="Delete"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md
                                   text-gray-500 hover:text-red-300 hover:bg-red-500/10
                                   transition-colors disabled:opacity-40">
                        {deletingId === inv.id
                          ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                          : <TrashIcon className="w-3.5 h-3.5" />
                        }
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-white/[0.06]">
            <Pagination page={page} totalPages={totalPages} total={total} label="codes" onPage={handlePage} />
          </div>
        )}
      </PanelCard>
      {confirmDeleteInvite && (
        <ConfirmModal
          title="Delete invite code?"
          message={`"${confirmDeleteInvite.code}" — this cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => deleteCode(confirmDeleteInvite)}
          onCancel={() => setConfirmDeleteInvite(null)}
        />
      )}
    </div>
  );
}
